import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Lock, Building2, MapPin, FileText, LogOut, 
  Check, X, Trash2, Eye, Clock, CheckCircle, XCircle,
  Plus, Edit, Save, Mail, BarChart3, Copy, TrendingUp,
  Users as UsersIcon, UserCheck, UserX, Briefcase, Plane,
  ClipboardCheck, Sparkles
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

const emptyHotel = {
  name: "", location: "", country: "", description: "", rating: "gold",
  image_url: "", certified_date: new Date().toISOString().split('T')[0],
  amenities: [], latitude: "", longitude: "", full_description: "",
  rooms: "", website: "", phone: "", email: ""
};

const emptyDestination = {
  name: "", country: "", description: "", rating: "gold",
  image_url: "", highlights: [], latitude: "", longitude: "",
  full_description: "", best_time_to_visit: "", currency: "", language: ""
};

// --- Badge Analytics Panel ---
const EVENT_LABELS = {
  view: "Page Views",
  copy_html: "HTML Copies",
  copy_markdown: "Markdown Copies",
  copy_image: "Image URL Copies",
  copy_profile: "Profile URL Copies",
};

const SIZE_LABELS = { small: "Small (80px)", medium: "Medium (120px)", large: "Large (180px)" };

const ratingPill = (rating) => {
  const cls = rating === "gold"
    ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]"
    : rating === "silver"
    ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]"
    : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {rating ? rating.charAt(0).toUpperCase() + rating.slice(1) : "—"}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-[#718096] font-semibold">{label}</span>
      <Icon className={`w-5 h-5 ${accent || "text-[#D4AF37]"}`} />
    </div>
    <div className="font-heading text-3xl font-bold text-[#0B132B]">{value}</div>
  </div>
);

const BarRow = ({ label, value, max, testid }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#4A5568]">{label}</span>
        <span className="font-semibold text-[#0B132B]">{value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const BadgeAnalyticsPanel = ({ analytics, loading, onRefresh }) => {
  if (loading && !analytics) {
    return (
      <div className="bg-white border border-gray-100 p-12 text-center">
        <div className="animate-spin w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
        <p className="text-[#718096] mt-4 text-sm">Loading analytics…</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white border border-gray-100 p-12 text-center" data-testid="analytics-empty">
        <BarChart3 className="w-12 h-12 text-[#718096] mx-auto mb-4" />
        <p className="text-[#718096] mb-4">No analytics data yet. Click refresh to load.</p>
        <Button onClick={onRefresh} variant="outline" data-testid="analytics-refresh-button">
          Refresh
        </Button>
      </div>
    );
  }

  const { total_views, total_copies, conversion_rate, by_event_type, by_size, top_listings } = analytics;
  const maxEvent = Math.max(1, ...by_event_type.map((e) => e.count));
  const maxSize = Math.max(1, ...by_size.map((s) => s.count));

  return (
    <div className="space-y-6" data-testid="badge-analytics-panel">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0B132B]">Badge Embed Analytics</h2>
          <p className="text-sm text-[#718096] mt-1">How certified listings are promoting their WTTQ badge</p>
        </div>
        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="analytics-refresh-button">
          <TrendingUp className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Eye} label="Total Views" value={total_views} />
        <StatCard icon={Copy} label="Total Copies" value={total_copies} />
        <StatCard icon={TrendingUp} label="Copy Rate" value={`${conversion_rate}%`} accent="text-emerald-600" />
      </div>

      {/* Breakdown grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg font-bold text-[#0B132B] mb-4">By Event Type</h3>
          {by_event_type.length === 0 ? (
            <p className="text-sm text-[#718096]">No events logged yet.</p>
          ) : (
            <div className="space-y-4">
              {by_event_type.map((e) => (
                <BarRow 
                  key={e.event_type} 
                  label={EVENT_LABELS[e.event_type] || e.event_type} 
                  value={e.count} 
                  max={maxEvent} 
                  testid={`event-row-${e.event_type}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg font-bold text-[#0B132B] mb-4">Popular Badge Size</h3>
          {by_size.length === 0 ? (
            <p className="text-sm text-[#718096]">No copy events with a size yet.</p>
          ) : (
            <div className="space-y-4">
              {by_size.map((s) => (
                <BarRow 
                  key={s.size} 
                  label={SIZE_LABELS[s.size] || s.size} 
                  value={s.count} 
                  max={maxSize} 
                  testid={`size-row-${s.size}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top listings table */}
      <div className="bg-white border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#0B132B]">Top Listings by Engagement</h3>
          <p className="text-xs text-[#718096] mt-1">Listings actively promoting their WTTQ certification</p>
        </div>
        {top_listings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[#718096] text-sm">No listings have embed activity yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="top-listings-table">
              <thead className="bg-[#F2F4F8] border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                  <th className="px-6 py-3">Listing</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Tier</th>
                  <th className="px-6 py-3 text-right">Views</th>
                  <th className="px-6 py-3 text-right">Copies</th>
                  <th className="px-6 py-3 text-right">Copy Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {top_listings.map((l) => (
                  <tr key={`${l.listing_type}-${l.listing_id}`} className="hover:bg-[#FAFAFA]" data-testid={`top-listing-${l.listing_id}`}>
                    <td className="px-6 py-4">
                      <Link 
                        to={`/${l.listing_type}/${l.listing_id}`}
                        className="font-medium text-[#0B132B] hover:text-[#D4AF37]"
                      >
                        {l.listing_name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4A5568] capitalize">{l.listing_type}</td>
                    <td className="px-6 py-4">{ratingPill(l.rating)}</td>
                    <td className="px-6 py-4 text-right font-medium text-[#0B132B]">{l.views}</td>
                    <td className="px-6 py-4 text-right font-medium text-[#0B132B]">{l.copies}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-emerald-600">{l.conversion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Users Panel ---
const ROLE_BADGE = {
  travel_partner: { label: "Travel Partner", cls: "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" },
  corporate_member: { label: "Corporate Member", cls: "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" },
  government_member: { label: "Government Member", cls: "bg-blue-50 border-blue-200 text-blue-700" },
  advisory_member: { label: "Advisory Member", cls: "bg-purple-50 border-purple-200 text-purple-700" },
  strategic_partner: { label: "Strategic Partner", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  innovative_partner: { label: "Innovative Partner", cls: "bg-sky-50 border-sky-200 text-sky-700" },
  executive_board: { label: "Executive Board", cls: "bg-[#0B132B] border-[#0B132B] text-white" },
  sponsor: { label: "Sponsor", cls: "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]" },
  investor: { label: "Investor", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  traveler: { label: "Traveller", cls: "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" },
};

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-amber-50 border-amber-200 text-amber-700", icon: Clock },
  approved: { label: "Approved", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rejected", cls: "bg-red-50 border-red-200 text-red-700", icon: XCircle },
};

const UsersPanel = ({ users, loading, onRefresh, onUpdateStatus, onDelete }) => {
  const [filter, setFilter] = useState("all");
  const pendingCount = users.filter((u) => u.status === "pending").length;
  const approvedCount = users.filter((u) => u.status === "approved").length;
  const rejectedCount = users.filter((u) => u.status === "rejected").length;
  const filtered = filter === "all" ? users : users.filter((u) => u.status === filter);

  if (loading && users.length === 0) {
    return (
      <div className="bg-white border border-gray-100 p-12 text-center">
        <div className="animate-spin w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
        <p className="text-[#718096] mt-4 text-sm">Loading users…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-panel">
      {/* Header + stats */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0B132B]">User Accounts</h2>
          <p className="text-sm text-[#718096] mt-1">Review and approve new registrations</p>
        </div>
        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="users-refresh-button">
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[#718096] font-semibold">Pending</span>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="font-heading text-2xl font-bold text-[#0B132B] mt-2" data-testid="users-count-pending">{pendingCount}</div>
        </div>
        <div className="bg-white border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[#718096] font-semibold">Approved</span>
            <UserCheck className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="font-heading text-2xl font-bold text-[#0B132B] mt-2" data-testid="users-count-approved">{approvedCount}</div>
        </div>
        <div className="bg-white border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-[#718096] font-semibold">Rejected</span>
            <UserX className="w-4 h-4 text-red-600" />
          </div>
          <div className="font-heading text-2xl font-bold text-[#0B132B] mt-2" data-testid="users-count-rejected">{rejectedCount}</div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { val: "all", label: `All (${users.length})` },
          { val: "pending", label: `Pending (${pendingCount})` },
          { val: "approved", label: `Approved (${approvedCount})` },
          { val: "rejected", label: `Rejected (${rejectedCount})` },
        ].map((f) => (
          <button
            key={f.val}
            onClick={() => setFilter(f.val)}
            className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
              filter === f.val
                ? "bg-[#0B132B] text-white border-[#0B132B]"
                : "bg-white text-[#4A5568] border-gray-200 hover:border-[#D4AF37]"
            }`}
            data-testid={`users-filter-${f.val}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <UsersIcon className="w-12 h-12 text-[#718096] mx-auto mb-4" />
            <p className="text-[#718096] text-sm">No users in this view</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-[#F2F4F8] border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Role</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Joined</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((u) => {
                  const role = ROLE_BADGE[u.role] || { label: u.role, cls: "bg-gray-50 border-gray-200 text-gray-700" };
                  const status = STATUS_BADGE[u.status] || { label: u.status, cls: "bg-gray-50 border-gray-200 text-gray-700", icon: Clock };
                  const StatusIcon = status.icon;
                  return (
                    <tr key={u.id} className="hover:bg-[#FAFAFA]" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4 font-medium text-[#0B132B]">{u.full_name}</td>
                      <td className="px-6 py-4 text-sm text-[#4A5568] break-all">{u.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${role.cls}`}>
                          <Briefcase className="w-3 h-3" /> {role.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${status.cls}`}>
                          <StatusIcon className="w-3 h-3" /> {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#4A5568]">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          {u.status !== "approved" && (
                            <button
                              onClick={() => onUpdateStatus(u.id, "approved")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-md"
                              data-testid={`approve-user-${u.id}`}
                            >
                              <Check className="w-3 h-3" /> Approve
                            </button>
                          )}
                          {u.status !== "rejected" && (
                            <button
                              onClick={() => onUpdateStatus(u.id, "rejected")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-md"
                              data-testid={`reject-user-${u.id}`}
                            >
                              <X className="w-3 h-3" /> Reject
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete ${u.full_name}? This cannot be undone.`)) onDelete(u.id);
                            }}
                            className="p-1.5 hover:bg-red-50 rounded-md"
                            data-testid={`delete-user-${u.id}`}
                            aria-label="Delete user"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const tierColor = (label) => {
  if (label?.startsWith("Gold")) return { bg: "#FBF3DC", fg: "#8a5e08", border: "#E8C870" };
  if (label?.startsWith("Silver")) return { bg: "#EEF1F4", fg: "#475569", border: "#CBD5E1" };
  if (label?.startsWith("Bronze")) return { bg: "#FBEEE3", fg: "#8b4a18", border: "#E5B891" };
  if (label?.startsWith("Not")) return { bg: "#FEF2F2", fg: "#9b2c2c", border: "#FCA5A5" };
  return { bg: "#F1F5F9", fg: "#64748B", border: "#CBD5E1" };
};

const AssessmentsPanel = ({ assessments, loading, onRefresh }) => {
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="assessments-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-[#0B132B]">Self-Assessments</h3>
          <p className="text-sm text-[#718096] mt-1">
            Submitted and in-progress questionnaires from registered partners.
          </p>
        </div>
        <Button onClick={onRefresh} variant="outline" data-testid="assessments-refresh">
          Refresh
        </Button>
      </div>

      <div className="bg-white border border-gray-100">
        {loading ? (
          <div className="p-12 text-center text-[#718096]">Loading...</div>
        ) : assessments.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardCheck className="w-12 h-12 text-[#718096] mx-auto mb-4" />
            <p className="text-[#718096]">No assessments yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="assessments-table">
              <thead className="bg-[#F2F4F8] border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Partner</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Score</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Tier</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Completion</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Updated</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => {
                  const c = tierColor(a.certification_label);
                  return (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`assessment-row-${a.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#0B132B]">{a.user_full_name || "—"}</div>
                        <div className="text-xs text-[#718096]">{a.user_email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-2xl font-semibold text-[#0B132B]">{a.overall ?? 0}</span>
                        <span className="text-xs text-[#718096]"> /100</span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold border"
                          style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                        >
                          {a.certification_label || "In Progress"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0B132B]">{a.completion ?? 0}%</td>
                      <td className="px-6 py-4">
                        {a.submitted ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2e5b3f]">
                            <CheckCircle className="w-3.5 h-3.5" /> Submitted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#718096]">
                            <Clock className="w-3.5 h-3.5" /> Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-[#718096]">
                        {a.updated_at ? new Date(a.updated_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelected(a)}
                          data-testid={`assessment-view-${a.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selected.user_full_name || selected.user_email}</DialogTitle>
                <DialogDescription>
                  {selected.user_email} · Updated {selected.updated_at ? new Date(selected.updated_at).toLocaleString() : "—"}
                </DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] pr-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
                  <div className="p-4 bg-[#F8FAFC] border border-gray-100 rounded">
                    <p className="text-xs uppercase tracking-wide text-[#718096]">Score</p>
                    <p className="text-3xl font-semibold text-[#0B132B] mt-1">{selected.overall}</p>
                  </div>
                  <div className="p-4 bg-[#F8FAFC] border border-gray-100 rounded">
                    <p className="text-xs uppercase tracking-wide text-[#718096]">Tier</p>
                    <p className="text-sm font-semibold text-[#0B132B] mt-2">{selected.certification_label}</p>
                  </div>
                  <div className="p-4 bg-[#F8FAFC] border border-gray-100 rounded">
                    <p className="text-xs uppercase tracking-wide text-[#718096]">Completion</p>
                    <p className="text-3xl font-semibold text-[#0B132B] mt-1">{selected.completion}%</p>
                  </div>
                  <div className="p-4 bg-[#F8FAFC] border border-gray-100 rounded">
                    <p className="text-xs uppercase tracking-wide text-[#718096]">Status</p>
                    <p className="text-sm font-semibold mt-2" style={{ color: selected.submitted ? "#2e5b3f" : "#718096" }}>
                      {selected.submitted ? "Submitted" : "Draft"}
                    </p>
                  </div>
                </div>

                <h4 className="text-sm font-semibold text-[#0B132B] mb-3 mt-6">Per-Pillar Scores</h4>
                <div className="space-y-3">
                  {Object.entries(selected.category_percents || {}).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="capitalize text-[#0B132B]">{k.replace(/-/g, " ")}</span>
                        <span className="font-semibold">{Math.round(v)}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full"
                          style={{ width: `${v}%`, background: "linear-gradient(90deg,#b89158,#d6b679)" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {selected.auditor && Object.keys(selected.auditor).length > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-[#0B132B] mb-3 mt-6">Gold Excellence Review</h4>
                    <div className="space-y-1.5 text-sm">
                      {Object.entries(selected.auditor).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-2">
                          {v ? (
                            <CheckCircle className="w-4 h-4 text-[#4a7b68]" />
                          ) : (
                            <XCircle className="w-4 h-4 text-[#cbd5e1]" />
                          )}
                          <span className={v ? "text-[#0B132B]" : "text-[#718096]"}>{k}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </ScrollArea>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


const ALLIANCE_STATUS_BADGE = {
  new: { label: "New", cls: "bg-blue-50 border-blue-200 text-blue-700", icon: Sparkles },
  contacted: { label: "Contacted", cls: "bg-amber-50 border-amber-200 text-amber-700", icon: Mail },
  approved: { label: "Approved", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rejected", cls: "bg-red-50 border-red-200 text-red-700", icon: XCircle },
};

const ALLIANCE_TIERS = [
  "Corporate Member",
  "Government Member",
  "Strategic Partner",
  "Advisory Member",
  "Media Partner",
  "Innovative Partner",
  "Executive Board",
];

const AlliancePanel = ({ applications, loading, onRefresh, onUpdateStatus, onUpdateTier, onDelete }) => {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const counts = applications.reduce(
    (acc, a) => {
      const s = a.status || "new";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    {}
  );
  const filtered = filter === "all" ? applications : applications.filter((a) => (a.status || "new") === filter);

  return (
    <div data-testid="alliance-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-[#0B132B]">Alliance Membership Applications</h3>
          <p className="text-sm text-[#718096] mt-1">
            Inquiries submitted via the Global Alliance landing page.
          </p>
        </div>
        <Button onClick={onRefresh} variant="outline" data-testid="alliance-refresh">
          Refresh
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {[
          { val: "all", label: `All (${applications.length})` },
          { val: "new", label: `New (${counts.new || 0})` },
          { val: "contacted", label: `Contacted (${counts.contacted || 0})` },
          { val: "approved", label: `Approved (${counts.approved || 0})` },
          { val: "rejected", label: `Rejected (${counts.rejected || 0})` },
        ].map((f) => (
          <button
            key={f.val}
            onClick={() => setFilter(f.val)}
            className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
              filter === f.val
                ? "bg-[#0B132B] text-white border-[#0B132B]"
                : "bg-white text-[#4A5568] border-gray-200 hover:border-[#D4AF37]"
            }`}
            data-testid={`alliance-filter-${f.val}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-100">
        {loading ? (
          <div className="p-12 text-center text-[#718096]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Sparkles className="w-12 h-12 text-[#718096] mx-auto mb-4" />
            <p className="text-[#718096]">No alliance applications yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="alliance-table">
              <thead className="bg-[#F2F4F8] border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Business</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Location</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Tier</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Submitted</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-[#0B132B]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const status = ALLIANCE_STATUS_BADGE[a.status || "new"] || ALLIANCE_STATUS_BADGE.new;
                  const StatusIcon = status.icon;
                  return (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`alliance-row-${a.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#0B132B]">{a.business_name}</div>
                        <div className="text-xs text-[#718096]">{a.job_position}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#0B132B]">{a.contact_person}</div>
                        <div className="text-xs text-[#718096]">{a.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#4A5568]">
                        {a.city}{a.city && a.country ? ", " : ""}{a.country}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <Select
                          value={ALLIANCE_TIERS.includes(a.membership_tier) ? a.membership_tier : ""}
                          onValueChange={(v) => onUpdateTier(a.id, v)}
                        >
                          <SelectTrigger
                            className="h-8 text-xs border-[#E8D38E] bg-[#FDF9EC] text-[#92710d] font-semibold min-w-[160px]"
                            data-testid={`alliance-tier-select-${a.id}`}
                          >
                            <SelectValue placeholder={a.membership_tier || "Select tier"} />
                          </SelectTrigger>
                          <SelectContent>
                            {ALLIANCE_TIERS.map((t) => (
                              <SelectItem key={t} value={t} className="text-xs">
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${status.cls}`}>
                          <StatusIcon className="w-3 h-3" /> {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-[#718096]">
                        {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setSelected(a)}
                            className="p-2 hover:bg-gray-100 rounded"
                            title="View details"
                            data-testid={`alliance-view-${a.id}`}
                          >
                            <Eye className="w-4 h-4 text-[#718096]" />
                          </button>
                          {(a.status || "new") !== "approved" && (
                            <button
                              onClick={() => onUpdateStatus(a.id, "approved")}
                              className="p-2 hover:bg-emerald-50 rounded"
                              title="Mark approved"
                              data-testid={`alliance-approve-${a.id}`}
                            >
                              <Check className="w-4 h-4 text-emerald-600" />
                            </button>
                          )}
                          {(a.status || "new") !== "contacted" && (a.status || "new") !== "approved" && (
                            <button
                              onClick={() => onUpdateStatus(a.id, "contacted")}
                              className="p-2 hover:bg-amber-50 rounded"
                              title="Mark contacted"
                              data-testid={`alliance-contacted-${a.id}`}
                            >
                              <Mail className="w-4 h-4 text-amber-600" />
                            </button>
                          )}
                          {(a.status || "new") !== "rejected" && (
                            <button
                              onClick={() => onUpdateStatus(a.id, "rejected")}
                              className="p-2 hover:bg-red-50 rounded"
                              title="Reject"
                              data-testid={`alliance-reject-${a.id}`}
                            >
                              <X className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete application from ${a.business_name}?`)) onDelete(a.id);
                            }}
                            className="p-2 hover:bg-red-50 rounded"
                            title="Delete"
                            data-testid={`alliance-delete-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selected.business_name}</DialogTitle>
                <DialogDescription>
                  Submitted {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs uppercase text-[#718096] tracking-wide">Contact</p>
                    <p className="font-medium text-[#0B132B]">{selected.contact_person}</p>
                    <p className="text-xs text-[#718096]">{selected.job_position}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-[#718096] tracking-wide">Membership Tier</p>
                    <p className="font-medium text-[#0B132B]">{selected.membership_tier || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-[#718096] tracking-wide">Email</p>
                    <a href={`mailto:${selected.email}`} className="font-medium text-[#0B132B] hover:text-[#D4AF37] break-all">
                      {selected.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-[#718096] tracking-wide">Phone</p>
                    <p className="font-medium text-[#0B132B]">{selected.phone || "—"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs uppercase text-[#718096] tracking-wide">Location</p>
                    <p className="font-medium text-[#0B132B]">
                      {selected.city}{selected.city && selected.country ? ", " : ""}{selected.country}
                    </p>
                  </div>
                </div>
                {selected.message && (
                  <div>
                    <p className="text-xs uppercase text-[#718096] tracking-wide mb-1">Message</p>
                    <p className="text-[#4A5568] bg-[#F2F4F8] p-3 rounded whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form states
  const [hotelFormOpen, setHotelFormOpen] = useState(false);
  const [destFormOpen, setDestFormOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [editingDest, setEditingDest] = useState(null);
  const [hotelForm, setHotelForm] = useState(emptyHotel);
  const [destForm, setDestForm] = useState(emptyDestination);
  const [amenitiesInput, setAmenitiesInput] = useState("");
  const [highlightsInput, setHighlightsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [allianceApps, setAllianceApps] = useState([]);
  const [allianceLoading, setAllianceLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!adminPassword) return;
    
    try {
      const [appsRes, hotelsRes, destsRes, airlinesRes, statsRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers: { "x-admin-password": adminPassword } }),
        axios.get(`${API}/hotels`),
        axios.get(`${API}/destinations`),
        axios.get(`${API}/airlines`),
        axios.get(`${API}/stats`)
      ]);
      setApplications(appsRes.data);
      setHotels(hotelsRes.data);
      setDestinations(destsRes.data);
      setAirlines(airlinesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setAdminPassword("");
        toast.error("Session expired. Please login again.");
      }
    }
  }, [adminPassword]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      if (!cancelled) await fetchData();
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, fetchData]);

  const fetchAnalytics = useCallback(async () => {
    if (!adminPassword) return;
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/badge-analytics`, {
        headers: { "x-admin-password": adminPassword }
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load badge analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [adminPassword]);

  const fetchUsers = useCallback(async () => {
    if (!adminPassword) return;
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { "x-admin-password": adminPassword }
      });
      setUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [adminPassword]);

  const fetchAssessments = useCallback(async () => {
    if (!adminPassword) return;
    setAssessmentsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/assessments`, {
        headers: { "x-admin-password": adminPassword }
      });
      setAssessments(res.data);
    } catch (error) {
      console.error("Error fetching assessments:", error);
      toast.error("Failed to load assessments");
    } finally {
      setAssessmentsLoading(false);
    }
  }, [adminPassword]);

  const fetchAllianceApps = useCallback(async () => {
    if (!adminPassword) return;
    setAllianceLoading(true);
    try {
      const res = await axios.get(`${API}/admin/alliance/applications`, {
        headers: { "x-admin-password": adminPassword }
      });
      setAllianceApps(res.data);
    } catch (error) {
      console.error("Error fetching alliance applications:", error);
      toast.error("Failed to load alliance applications");
    } finally {
      setAllianceLoading(false);
    }
  }, [adminPassword]);

  const updateAllianceStatus = async (appId, status) => {
    try {
      await axios.put(
        `${API}/admin/alliance/applications/${appId}/status`,
        { status },
        { headers: { "x-admin-password": adminPassword } }
      );
      toast.success(`Application marked ${status}`);
      fetchAllianceApps();
    } catch (error) {
      toast.error("Failed to update application");
    }
  };

  const updateAllianceTier = async (appId, tier) => {
    try {
      await axios.put(
        `${API}/admin/alliance/applications/${appId}/tier`,
        { membership_tier: tier },
        { headers: { "x-admin-password": adminPassword } }
      );
      toast.success(`Tier updated to ${tier}`);
      fetchAllianceApps();
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const deleteAllianceApp = async (appId) => {
    try {
      await axios.delete(`${API}/admin/alliance/applications/${appId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Application deleted");
      fetchAllianceApps();
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status }, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success(`User ${status}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const publishHotel = async (hotelId) => {
    try {
      await axios.put(`${API}/hotels/${hotelId}`, { review_status: "published" }, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Hotel published");
      fetchData();
    } catch (error) {
      toast.error("Failed to publish hotel");
    }
  };

  const publishDestination = async (destId) => {
    try {
      await axios.put(`${API}/destinations/${destId}`, { review_status: "published" }, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Destination published");
      fetchData();
    } catch (error) {
      toast.error("Failed to publish destination");
    }
  };

  const publishAirline = async (airlineId) => {
    try {
      await axios.put(`${API}/airlines/${airlineId}`, { review_status: "published" }, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Airline published");
      fetchData();
    } catch (error) {
      toast.error("Failed to publish airline");
    }
  };

  const handleDeleteAirline = async (airlineId) => {
    try {
      await axios.delete(`${API}/airlines/${airlineId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Airline deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete airline");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/verify`, { email, password });
      setAdminPassword(password);
      setIsAuthenticated(true);
      toast.success("Admin access granted");
    } catch (error) {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminPassword("");
    setEmail("");
    setPassword("");
    toast.info("Logged out successfully");
  };

  const updateApplicationStatus = async (appId, status) => {
    try {
      await axios.put(
        `${API}/applications/${appId}/status`,
        { status },
        { headers: { "x-admin-password": adminPassword } }
      );
      toast.success(`Application ${status}`, {
        description: "Email notification will be sent if configured."
      });
      fetchData();
      setSelectedApp(null);
    } catch (error) {
      toast.error("Failed to update application");
    }
  };

  const deleteApplication = async (appId) => {
    try {
      await axios.delete(`${API}/applications/${appId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Application deleted");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  const deleteHotel = async (hotelId) => {
    try {
      await axios.delete(`${API}/hotels/${hotelId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Hotel deleted");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Failed to delete hotel");
    }
  };

  const deleteDestination = async (destId) => {
    try {
      await axios.delete(`${API}/destinations/${destId}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Destination deleted");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Failed to delete destination");
    }
  };

  // Hotel form handlers
  const openNewHotelForm = () => {
    setEditingHotel(null);
    setHotelForm(emptyHotel);
    setAmenitiesInput("");
    setHotelFormOpen(true);
  };

  const openEditHotelForm = (hotel) => {
    setEditingHotel(hotel);
    setHotelForm({
      ...hotel,
      latitude: hotel.latitude || "",
      longitude: hotel.longitude || "",
      rooms: hotel.rooms || "",
      amenities: hotel.amenities || []
    });
    setAmenitiesInput(hotel.amenities?.join(", ") || "");
    setHotelFormOpen(true);
  };

  const handleHotelFormChange = (field, value) => {
    setHotelForm(prev => ({ ...prev, [field]: value }));
  };

  const saveHotel = async () => {
    setSaving(true);
    try {
      const amenities = amenitiesInput.split(",").map(a => a.trim()).filter(a => a);
      const payload = {
        ...hotelForm,
        amenities,
        latitude: hotelForm.latitude ? parseFloat(hotelForm.latitude) : null,
        longitude: hotelForm.longitude ? parseFloat(hotelForm.longitude) : null,
        rooms: hotelForm.rooms ? parseInt(hotelForm.rooms) : null
      };

      if (editingHotel) {
        await axios.put(`${API}/hotels/${editingHotel.id}`, payload, {
          headers: { "x-admin-password": adminPassword }
        });
        toast.success("Hotel updated successfully");
      } else {
        await axios.post(`${API}/hotels`, payload, {
          headers: { "x-admin-password": adminPassword }
        });
        toast.success("Hotel created successfully");
      }
      setHotelFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save hotel");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Destination form handlers
  const openNewDestForm = () => {
    setEditingDest(null);
    setDestForm(emptyDestination);
    setHighlightsInput("");
    setDestFormOpen(true);
  };

  const openEditDestForm = (dest) => {
    setEditingDest(dest);
    setDestForm({
      ...dest,
      latitude: dest.latitude || "",
      longitude: dest.longitude || "",
      highlights: dest.highlights || []
    });
    setHighlightsInput(dest.highlights?.join(", ") || "");
    setDestFormOpen(true);
  };

  const handleDestFormChange = (field, value) => {
    setDestForm(prev => ({ ...prev, [field]: value }));
  };

  const saveDestination = async () => {
    setSaving(true);
    try {
      const highlights = highlightsInput.split(",").map(h => h.trim()).filter(h => h);
      const payload = {
        ...destForm,
        highlights,
        latitude: destForm.latitude ? parseFloat(destForm.latitude) : null,
        longitude: destForm.longitude ? parseFloat(destForm.longitude) : null
      };

      if (editingDest) {
        await axios.put(`${API}/destinations/${editingDest.id}`, payload, {
          headers: { "x-admin-password": adminPassword }
        });
        toast.success("Destination updated successfully");
      } else {
        await axios.post(`${API}/destinations`, payload, {
          headers: { "x-admin-password": adminPassword }
        });
        toast.success("Destination created successfully");
      }
      setDestFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save destination");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200"
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0B132B] flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="bg-white p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#0B132B]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#0B132B] mb-2">Admin Access</h1>
            <p className="text-[#718096] text-sm">Enter your credentials to continue</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <Label htmlFor="email" className="text-[#0B132B] font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="admin-email-input"
              />
            </div>
            <div className="mb-6">
              <Label htmlFor="password" className="text-[#0B132B] font-medium mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-[#0B132B] text-white py-3 font-semibold hover:bg-[#1C2541] transition-colors disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loading ? "Verifying..." : "Login"}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/" className="text-[#D4AF37] hover:text-[#92710d] text-sm" data-testid="back-to-site-link">
              ← Back to Site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-heading text-2xl font-bold tracking-tight">
                <span className="text-white">WTT</span>
                <span className="text-[#D4AF37]">Q</span>
                <span className="text-white">.org</span>
              </span>
              <span className="text-white/40">|</span>
              <div>
                <span className="font-heading text-lg font-bold text-white">Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="text-white/70 hover:text-white text-sm"
                data-testid="view-site-link"
              >
                View Site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white p-6 border border-gray-100">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="text-2xl font-bold text-[#0B132B]">{stats.total_hotels}</p>
                  <p className="text-sm text-[#718096]">Hotels</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 border border-gray-100">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="text-2xl font-bold text-[#0B132B]">{stats.total_destinations}</p>
                  <p className="text-sm text-[#718096]">Destinations</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 border border-gray-100">
              <div className="flex items-center gap-3">
                <Plane className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="text-2xl font-bold text-[#0B132B]">{stats.total_airlines || 0}</p>
                  <p className="text-sm text-[#718096]">Airlines</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 border border-gray-100">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="text-2xl font-bold text-[#0B132B]">{applications.length}</p>
                  <p className="text-sm text-[#718096]">Applications</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 border border-gray-100">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-[#0B132B]">{stats.pending_applications || 0}</p>
                  <p className="text-sm text-[#718096]">Pending</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <Tabs defaultValue="applications" className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 mb-6">
            <TabsTrigger 
              value="applications"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-applications"
            >
              Applications ({applications.length})
            </TabsTrigger>
            <TabsTrigger 
              value="hotels"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-hotels"
            >
              Hotels ({hotels.length})
            </TabsTrigger>
            <TabsTrigger 
              value="destinations"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-destinations"
            >
              Destinations ({destinations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="airlines"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-airlines"
            >
              <Plane className="w-4 h-4 mr-1.5" /> Airlines ({airlines.length})
            </TabsTrigger>
            <TabsTrigger 
              value="analytics"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-analytics"
              onClick={fetchAnalytics}
            >
              <BarChart3 className="w-4 h-4 mr-1.5" /> Badge Analytics
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-users"
              onClick={fetchUsers}
            >
              <UsersIcon className="w-4 h-4 mr-1.5" /> Users
            </TabsTrigger>
            <TabsTrigger 
              value="assessments"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-assessments"
              onClick={fetchAssessments}
            >
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Assessments
            </TabsTrigger>
            <TabsTrigger 
              value="alliance"
              className="data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-2"
              data-testid="tab-alliance"
              onClick={fetchAllianceApps}
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Alliance Applications
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white border border-gray-100">
              {applications.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-[#718096] mx-auto mb-4" />
                  <p className="text-[#718096]">No applications yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="applications-table">
                    <thead className="bg-[#F2F4F8] border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Hotel Name</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Contact</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Location</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Submitted</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-[#0B132B]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4 font-medium text-[#0B132B]">{app.hotel_name}</td>
                          <td className="px-6 py-4">
                            <p className="text-[#0B132B]">{app.contact_name}</p>
                            <p className="text-sm text-[#718096]">{app.email}</p>
                          </td>
                          <td className="px-6 py-4 text-[#4A5568]">{app.location}, {app.country}</td>
                          <td className="px-6 py-4">{getStatusBadge(app.status)}</td>
                          <td className="px-6 py-4 text-sm text-[#718096]">
                            {new Date(app.submitted_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedApp(app)}
                                className="p-2 hover:bg-gray-100 rounded"
                                title="View Details"
                                data-testid={`view-app-${app.id}`}
                              >
                                <Eye className="w-4 h-4 text-[#718096]" />
                              </button>
                              {app.status === "pending" && (
                                <>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, "approved")}
                                    className="p-2 hover:bg-green-50 rounded"
                                    title="Approve"
                                    data-testid={`approve-app-${app.id}`}
                                  >
                                    <Check className="w-4 h-4 text-green-600" />
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, "rejected")}
                                    className="p-2 hover:bg-red-50 rounded"
                                    title="Reject"
                                    data-testid={`reject-app-${app.id}`}
                                  >
                                    <X className="w-4 h-4 text-red-600" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => setDeleteConfirm({ type: "application", id: app.id, name: app.hotel_name })}
                                className="p-2 hover:bg-red-50 rounded"
                                title="Delete"
                                data-testid={`delete-app-${app.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Hotels Tab */}
          <TabsContent value="hotels">
            <div className="mb-4">
              <Button onClick={openNewHotelForm} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-hotel-button">
                <Plus className="w-4 h-4 mr-2" /> Add New Hotel
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hotels.map((hotel) => (
                <div key={hotel.id} className="bg-white border border-gray-100 overflow-hidden group" data-testid={`admin-hotel-${hotel.id}`}>
                  <div className="relative h-40">
                    <img src={hotel.image_url} alt={hotel.name} className="w-full h-full object-cover" />
                    {hotel.review_status === "pending_review" && (
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold shadow">
                          <Clock className="w-2.5 h-2.5" /> Pending Review
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <img src={BADGE_URLS[hotel.rating]} alt={hotel.rating} className="w-10 h-10" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-[#0B132B] mb-1">{hotel.name}</h3>
                    <p className="text-sm text-[#718096] mb-3">{hotel.location}, {hotel.country}</p>
                    {hotel.review_status === "pending_review" && (
                      <button
                        onClick={() => publishHotel(hotel.id)}
                        className="w-full mb-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-md"
                        data-testid={`publish-hotel-${hotel.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditHotelForm(hotel)}
                          className="p-2 hover:bg-blue-50 rounded"
                          title="Edit"
                          data-testid={`edit-hotel-${hotel.id}`}
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <Link 
                          to={`/hotel/${hotel.id}`}
                          className="p-2 hover:bg-gray-100 rounded"
                          title="View"
                        >
                          <Eye className="w-4 h-4 text-[#718096]" />
                        </Link>
                      </div>
                      <button
                        onClick={() => setDeleteConfirm({ type: "hotel", id: hotel.id, name: hotel.name })}
                        className="p-2 hover:bg-red-50 rounded"
                        data-testid={`delete-hotel-${hotel.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Destinations Tab */}
          <TabsContent value="destinations">
            <div className="mb-4">
              <Button onClick={openNewDestForm} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-destination-button">
                <Plus className="w-4 h-4 mr-2" /> Add New Destination
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {destinations.map((dest) => (
                <div key={dest.id} className="bg-white border border-gray-100 overflow-hidden group" data-testid={`admin-dest-${dest.id}`}>
                  <div className="relative h-40">
                    <img src={dest.image_url} alt={dest.name} className="w-full h-full object-cover" />
                    {dest.review_status === "pending_review" && (
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold shadow">
                          <Clock className="w-2.5 h-2.5" /> Pending Review
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <img src={BADGE_URLS[dest.rating]} alt={dest.rating} className="w-10 h-10" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-[#0B132B] mb-1">{dest.name}</h3>
                    <p className="text-sm text-[#718096] mb-3">{dest.country}</p>
                    {dest.review_status === "pending_review" && (
                      <button
                        onClick={() => publishDestination(dest.id)}
                        className="w-full mb-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-md"
                        data-testid={`publish-dest-${dest.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditDestForm(dest)}
                          className="p-2 hover:bg-blue-50 rounded"
                          title="Edit"
                          data-testid={`edit-dest-${dest.id}`}
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <Link 
                          to={`/destination/${dest.id}`}
                          className="p-2 hover:bg-gray-100 rounded"
                          title="View"
                        >
                          <Eye className="w-4 h-4 text-[#718096]" />
                        </Link>
                      </div>
                      <button
                        onClick={() => setDeleteConfirm({ type: "destination", id: dest.id, name: dest.name })}
                        className="p-2 hover:bg-red-50 rounded"
                        data-testid={`delete-dest-${dest.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Airlines Tab */}
          <TabsContent value="airlines">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {airlines.map((airline) => (
                <div key={airline.id} className="bg-white border border-gray-100 overflow-hidden group" data-testid={`admin-airline-${airline.id}`}>
                  <div className="relative h-40">
                    <img src={airline.image_url} alt={airline.name} className="w-full h-full object-cover" />
                    {airline.review_status === "pending_review" && (
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold shadow">
                          <Clock className="w-2.5 h-2.5" /> Pending Review
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <img src={BADGE_URLS[airline.rating]} alt={airline.rating} className="w-10 h-10" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-[#0B132B] mb-1">{airline.name}</h3>
                    <p className="text-sm text-[#718096] mb-3 flex items-center gap-1">
                      <Plane className="w-3.5 h-3.5" /> {airline.country}
                      {airline.fleet_size && <span className="ml-2 text-xs">· Fleet: {airline.fleet_size}</span>}
                    </p>
                    {airline.review_status === "pending_review" && (
                      <button
                        onClick={() => publishAirline(airline.id)}
                        className="w-full mb-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-md"
                        data-testid={`publish-airline-${airline.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                    <div className="flex items-center justify-between">
                      <Link 
                        to={`/airline/${airline.id}`}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="View"
                      >
                        <Eye className="w-4 h-4 text-[#718096]" />
                      </Link>
                      <button
                        onClick={() => setDeleteConfirm({ type: "airline", id: airline.id, name: airline.name })}
                        className="p-2 hover:bg-red-50 rounded"
                        data-testid={`delete-airline-${airline.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {airlines.length === 0 && (
                <div className="col-span-full text-center py-12 text-[#718096] text-sm">No airlines yet.</div>
              )}
            </div>
          </TabsContent>

          {/* Badge Analytics Tab */}
          <TabsContent value="analytics">
            <BadgeAnalyticsPanel 
              analytics={analytics}
              loading={analyticsLoading}
              onRefresh={fetchAnalytics}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersPanel 
              users={users}
              loading={usersLoading}
              onRefresh={fetchUsers}
              onUpdateStatus={updateUserStatus}
              onDelete={deleteUser}
            />
          </TabsContent>

          <TabsContent value="assessments">
            <AssessmentsPanel
              assessments={assessments}
              loading={assessmentsLoading}
              onRefresh={fetchAssessments}
            />
          </TabsContent>

          <TabsContent value="alliance">
            <AlliancePanel
              applications={allianceApps}
              loading={allianceLoading}
              onRefresh={fetchAllianceApps}
              onUpdateStatus={updateAllianceStatus}
              onUpdateTier={updateAllianceTier}
              onDelete={deleteAllianceApp}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{selectedApp?.hotel_name}</DialogTitle>
            <DialogDescription>Application Details</DialogDescription>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-center">
                <span className="text-[#718096]">Status</span>
                {getStatusBadge(selectedApp.status)}
              </div>
              <div>
                <span className="text-[#718096] text-sm">Contact Person</span>
                <p className="font-medium text-[#0B132B]">{selectedApp.contact_name}</p>
              </div>
              <div>
                <span className="text-[#718096] text-sm">Email</span>
                <p className="font-medium text-[#0B132B] flex items-center gap-2">
                  {selectedApp.email}
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                </p>
              </div>
              <div>
                <span className="text-[#718096] text-sm">Phone</span>
                <p className="font-medium text-[#0B132B]">{selectedApp.phone}</p>
              </div>
              <div>
                <span className="text-[#718096] text-sm">Location</span>
                <p className="font-medium text-[#0B132B]">{selectedApp.location}, {selectedApp.country}</p>
              </div>
              <div>
                <span className="text-[#718096] text-sm">Message</span>
                <p className="text-[#4A5568] bg-[#F2F4F8] p-3 rounded mt-1">{selectedApp.message}</p>
              </div>
              <div>
                <span className="text-[#718096] text-sm">Submitted</span>
                <p className="font-medium text-[#0B132B]">{new Date(selectedApp.submitted_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          {selectedApp?.status === "pending" && (
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => updateApplicationStatus(selectedApp.id, "rejected")}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                onClick={() => updateApplicationStatus(selectedApp.id, "approved")}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2" /> Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Hotel Form Modal */}
      <Dialog open={hotelFormOpen} onOpenChange={setHotelFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingHotel ? "Edit Hotel" : "Add New Hotel"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hotel Name *</Label>
                  <Input
                    value={hotelForm.name}
                    onChange={(e) => handleHotelFormChange("name", e.target.value)}
                    placeholder="Hotel name"
                    data-testid="hotel-form-name"
                  />
                </div>
                <div>
                  <Label>Rating *</Label>
                  <Select value={hotelForm.rating} onValueChange={(v) => handleHotelFormChange("rating", v)}>
                    <SelectTrigger data-testid="hotel-form-rating">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="bronze">Bronze</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location *</Label>
                  <Input
                    value={hotelForm.location}
                    onChange={(e) => handleHotelFormChange("location", e.target.value)}
                    placeholder="City/Area"
                    data-testid="hotel-form-location"
                  />
                </div>
                <div>
                  <Label>Country *</Label>
                  <Input
                    value={hotelForm.country}
                    onChange={(e) => handleHotelFormChange("country", e.target.value)}
                    placeholder="Country"
                    data-testid="hotel-form-country"
                  />
                </div>
              </div>
              <div>
                <Label>Short Description *</Label>
                <Textarea
                  value={hotelForm.description}
                  onChange={(e) => handleHotelFormChange("description", e.target.value)}
                  placeholder="Brief description for cards"
                  rows={2}
                  data-testid="hotel-form-description"
                />
              </div>
              <div>
                <Label>Full Description</Label>
                <Textarea
                  value={hotelForm.full_description}
                  onChange={(e) => handleHotelFormChange("full_description", e.target.value)}
                  placeholder="Detailed description for detail page"
                  rows={4}
                />
              </div>
              <div>
                <Label>Image URL *</Label>
                <Input
                  value={hotelForm.image_url}
                  onChange={(e) => handleHotelFormChange("image_url", e.target.value)}
                  placeholder="https://..."
                  data-testid="hotel-form-image"
                />
              </div>
              <div>
                <Label>Amenities (comma-separated)</Label>
                <Input
                  value={amenitiesInput}
                  onChange={(e) => setAmenitiesInput(e.target.value)}
                  placeholder="Spa, Pool, Restaurant, etc."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={hotelForm.latitude}
                    onChange={(e) => handleHotelFormChange("latitude", e.target.value)}
                    placeholder="e.g., 25.0767"
                  />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={hotelForm.longitude}
                    onChange={(e) => handleHotelFormChange("longitude", e.target.value)}
                    placeholder="e.g., 55.1337"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Number of Rooms</Label>
                  <Input
                    type="number"
                    value={hotelForm.rooms}
                    onChange={(e) => handleHotelFormChange("rooms", e.target.value)}
                    placeholder="e.g., 150"
                  />
                </div>
                <div>
                  <Label>Certified Date *</Label>
                  <Input
                    type="date"
                    value={hotelForm.certified_date}
                    onChange={(e) => handleHotelFormChange("certified_date", e.target.value)}
                    data-testid="hotel-form-date"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website</Label>
                  <Input
                    value={hotelForm.website}
                    onChange={(e) => handleHotelFormChange("website", e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={hotelForm.phone}
                    onChange={(e) => handleHotelFormChange("phone", e.target.value)}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={hotelForm.email}
                  onChange={(e) => handleHotelFormChange("email", e.target.value)}
                  placeholder="info@hotel.com"
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setHotelFormOpen(false)}>Cancel</Button>
            <Button 
              type="button"
              onClick={saveHotel} 
              disabled={saving || !hotelForm.name || !hotelForm.location || !hotelForm.country || !hotelForm.description || !hotelForm.image_url}
              className="bg-[#0B132B] hover:bg-[#1C2541]"
              data-testid="hotel-form-submit"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : editingHotel ? "Update Hotel" : "Create Hotel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Destination Form Modal */}
      <Dialog open={destFormOpen} onOpenChange={setDestFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingDest ? "Edit Destination" : "Add New Destination"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Destination Name *</Label>
                  <Input
                    value={destForm.name}
                    onChange={(e) => handleDestFormChange("name", e.target.value)}
                    placeholder="Destination name"
                    data-testid="dest-form-name"
                  />
                </div>
                <div>
                  <Label>Rating *</Label>
                  <Select value={destForm.rating} onValueChange={(v) => handleDestFormChange("rating", v)}>
                    <SelectTrigger data-testid="dest-form-rating">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="bronze">Bronze</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Country *</Label>
                <Input
                  value={destForm.country}
                  onChange={(e) => handleDestFormChange("country", e.target.value)}
                  placeholder="Country"
                  data-testid="dest-form-country"
                />
              </div>
              <div>
                <Label>Short Description *</Label>
                <Textarea
                  value={destForm.description}
                  onChange={(e) => handleDestFormChange("description", e.target.value)}
                  placeholder="Brief description for cards"
                  rows={2}
                  data-testid="dest-form-description"
                />
              </div>
              <div>
                <Label>Full Description</Label>
                <Textarea
                  value={destForm.full_description}
                  onChange={(e) => handleDestFormChange("full_description", e.target.value)}
                  placeholder="Detailed description for detail page"
                  rows={4}
                />
              </div>
              <div>
                <Label>Image URL *</Label>
                <Input
                  value={destForm.image_url}
                  onChange={(e) => handleDestFormChange("image_url", e.target.value)}
                  placeholder="https://..."
                  data-testid="dest-form-image"
                />
              </div>
              <div>
                <Label>Highlights (comma-separated)</Label>
                <Input
                  value={highlightsInput}
                  onChange={(e) => setHighlightsInput(e.target.value)}
                  placeholder="Beaches, Mountains, Culture, etc."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={destForm.latitude}
                    onChange={(e) => handleDestFormChange("latitude", e.target.value)}
                    placeholder="e.g., 3.2028"
                  />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={destForm.longitude}
                    onChange={(e) => handleDestFormChange("longitude", e.target.value)}
                    placeholder="e.g., 73.2207"
                  />
                </div>
              </div>
              <div>
                <Label>Best Time to Visit</Label>
                <Input
                  value={destForm.best_time_to_visit}
                  onChange={(e) => handleDestFormChange("best_time_to_visit", e.target.value)}
                  placeholder="e.g., November to April"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Currency</Label>
                  <Input
                    value={destForm.currency}
                    onChange={(e) => handleDestFormChange("currency", e.target.value)}
                    placeholder="e.g., Euro (EUR)"
                  />
                </div>
                <div>
                  <Label>Language</Label>
                  <Input
                    value={destForm.language}
                    onChange={(e) => handleDestFormChange("language", e.target.value)}
                    placeholder="e.g., English, Spanish"
                  />
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDestFormOpen(false)}>Cancel</Button>
            <Button 
              type="button"
              onClick={saveDestination} 
              disabled={saving || !destForm.name || !destForm.country || !destForm.description || !destForm.image_url}
              className="bg-[#0B132B] hover:bg-[#1C2541]"
              data-testid="dest-form-submit"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : editingDest ? "Update Destination" : "Create Destination"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirm?.type === "application") deleteApplication(deleteConfirm.id);
                else if (deleteConfirm?.type === "hotel") deleteHotel(deleteConfirm.id);
                else if (deleteConfirm?.type === "destination") deleteDestination(deleteConfirm.id);
                else if (deleteConfirm?.type === "airline") handleDeleteAirline(deleteConfirm.id);
              }}
              data-testid="confirm-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
